/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Stepper from '@/Components/Stepper';
import BaseModal from '@/Components/Modals/BaseModal/BaseModal';
import MultiPOGeneralDetails from '@/Components/Modals/MultiPoModal/MultiPOGeneralDetails';
import ConfirmationModal from '@/Components/Modals/MultiPoModal/ConfirmationModal';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import GlobalSearch from '@/Components/globalSearch/search';
import { modalActions } from '@/Actions';
import { notificationServices } from '@/Services';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import productionPlanServices from '@/Services/Analytics Services/production-plan.services';
import cookingRecipesServices from '@/Services/Form Services/cookingRecipes.services';
import { ProductionPlanModal } from '@/Components/Modals/ProductionPlanModal';
import { ClipboardPaste, AlertTriangle, Trash2, PlusCircle, Download } from 'lucide-react';
import { urls } from '@/Constants';
import useFilteredStores from '@/Helpers/useFilteredStores';
import {
	getUnitInLowerUnit, parsePastedTable, cleanPastedName, matchPastedRowToProduct,
	getUnitFamily, getHigherUnitAndQuantity,
} from '@/Helpers';
import { matchIngredientToProduct } from '@/Components/Modals/MultiRecipeEditModal/MultiRecipeEditModal';
import {
	headerLayoutConfig, STEPS, toNum, QTY_KEYS,
	buildLists, buildHeaderFields, labelToValue, buildVariantMetaMap, toHigherValue,
	buildColumns, renderCustomCol, stepColumnKeys, stepColumnOrder, editableColumnKeys, isClosingStockLocked, buildProcessColorMap,
	FormulaPopover, buildFooterButtons, renderFooterButtons, renderChips,
	InputCell, displayCell, getInitialState, toDisplayRow, getDefaultPlanningDate,
} from './InitiateProductionPlanModal.config';

const MODAL_TYPE = 'initiateProductionPlanModal';
const NOT_SCHEDULED_ROW_CLASS = [
	'[&>td:not(.sticky)]:relative',
	"[&>td]:after:content-['']",
	'[&>td]:after:absolute',
	'[&>td]:after:inset-x-0',
	'[&>td]:after:top-1/2',
	'[&>td]:after:border-t',
	'[&>td]:after:border-solid',
	'[&>td]:after:border-gray-700',
	'[&>td]:after:pointer-events-none',
	'[&>td]:after:z-30',
].join(' ');

const InitiateProductionPlanModal = (props) => {
	const { onInitiated } = props;
	const history = useHistory();
	const dispatch = useDispatch();
	const open = useSelector((state) => state?.modal?.[MODAL_TYPE]?.show);
	const modalData = useSelector((state) => state?.modal?.[MODAL_TYPE]?.data);
	const stores = useSelector((state) => state?.form?.STORES?.data?.LIST);
	const permittedStores = useSelector((state) => state?.navigation?.permittedStores);
	// Restrict "Select Kitchen" to base kitchens the logged-in user has access to.
	const filteredStores = useMemo(() => useFilteredStores(), [stores, permittedStores]);
	const productList = useSelector((state) => state?.form?.ALL_PRODUCTS?.data?.LIST);
	// All state starts from ONE initial-state map (config) — closing the modal
	// resets everything from the same source.
	const initialState = getInitialState();
	const [header, setHeader] = useState(initialState.header);
	const [search, setSearch] = useState(initialState.search);
	const [skuTypeFilter, setSkuTypeFilter] = useState('All');
	const [processFilter, setProcessFilter] = useState('All');
	// Tracks whether the user has manually edited the Plan Name. While false, the
	// Plan Name auto-follows the Planning Date.
	const planNameTouchedRef = useRef(false);
	// Tracks whether the user has manually picked a Planning Date. While false, the
	// default (current date, or +1 once past 11 AM) is applied on open.
	const dateTouchedRef = useRef(false);
	const [localShiftList, setLocalShiftList] = useState(initialState.localShiftList);
	const [confirmOpen, setConfirmOpen] = useState(initialState.confirmOpen);
	const [loading, setLoading] = useState(initialState.loading);
	const [showFormula, setShowFormula] = useState(initialState.showFormula);
	const [batchMap, setBatchMap] = useState(initialState.batchMap);
	const [step, setStep] = useState(initialState.step);
	const [maxStep, setMaxStep] = useState(initialState.maxStep);
	const [planId, setPlanId] = useState(initialState.planId);
	const [tableData, setTableData] = useState(initialState.tableData);
	const [stepTrack, setStepTrack] = useState(initialState.stepTrack);
	const [pastedIds, setPastedIds] = useState([]);
	const [notFoundModal, setNotFoundModal] = useState({ isOpen: false, items: [] });
	const [pendingPaste, setPendingPaste] = useState(null);
	const [notFoundRows, setNotFoundRows] = useState([]);
	const [ambiguousRows, setAmbiguousRows] = useState([]);

	const lists = useMemo(() => buildLists(filteredStores, localShiftList), [filteredStores, localShiftList]);

	const headerFields = useMemo(() => buildHeaderFields(lists), [lists]);

	const kitchenIdOf = (label) => labelToValue(lists.kitchenList, label);
	const shiftIdOf = (label) => labelToValue(lists.shiftList, label);
	const variantMetaMap = useMemo(() => buildVariantMetaMap(productList), [productList]);
	const displayRowOf = (r) => toDisplayRow(r, variantMetaMap, batchMap);

	useEffect(() => {
		if (!Object.keys(batchMap).length) return;
		setTableData((prev) => prev.map((r) => {
			if (r?.batchSize !== null && r?.batchSize !== undefined && r?.batchSize !== '') return r;
			const raw = batchMap[r?.variantId];
			if (raw == null) return r;
			return { ...r, batchSize: toHigherValue(r?.originalUnit, raw) };
		}));
	}, [batchMap]);

	const resetState = () => {
		const init = getInitialState();
		setHeader(init.header);
		setSearch(init.search);
		setSkuTypeFilter('All');
		setProcessFilter('All');
		planNameTouchedRef.current = false;
		dateTouchedRef.current = false;
		setLocalShiftList(init.localShiftList);
		setConfirmOpen(init.confirmOpen);
		setLoading(init.loading);
		setShowFormula(init.showFormula);
		setBatchMap(init.batchMap);
		setStep(init.step);
		setMaxStep(init.maxStep);
		setPlanId(init.planId);
		setTableData(init.tableData);
		setStepTrack(init.stepTrack);
		setPastedIds([]);
		setNotFoundModal({ isOpen: false, items: [] });
		setPendingPaste(null);
		setNotFoundRows([]);
		setAmbiguousRows([]);
	};

	const handleClose = () => {
		resetState();
		dispatch(modalActions.toggleModal(MODAL_TYPE, false));
	};
	const handleFetchApi = async (value) => {
		try {
			const res = await inventoryAnalyticsServices.fetchShifts({ params: { storeId: value } })
			if (res?.statusCode == 200) {
				const shifts = res?.data || [];
				setLocalShiftList(shifts);
				setHeader((prev) => ({ ...prev, shift: shifts.length === 1 ? (shifts[0]?.shiftName || shifts[0]?.name) : null }));
			} else {
				setLocalShiftList([]);
			}
		} catch (error) {
			console.error(error)
		}
		try {
			const batchRes = await cookingRecipesServices.fetchIngredientDirect({
				req: { params: { storeId: value } },
			});
			if (Array.isArray(batchRes?.data)) {
				const map = {};
				batchRes.data.forEach((b) => {
					if (b?.variantId != null && b?.batchSize != null) map[b.variantId] = b.batchSize;
				});
				setBatchMap(map);
			} else {
				setBatchMap({});
			}
		} catch (error) {
			console.error(error)
		}
	}

	const handleFieldChange = (fieldId, value) => {
		if (fieldId === 'kitchen') {
			handleFetchApi(kitchenIdOf(value));
		}
		if (fieldId === 'planName') {
			// User typed a name -> stop auto-deriving it from the date.
			planNameTouchedRef.current = true;
		}
		if (fieldId === 'planningDate') {
			// User picked a date -> stop auto-applying the default on open.
			dateTouchedRef.current = true;
		}
		setHeader((prev) => ({ ...prev, [fieldId]: value }));
	};

	useEffect(() => {
		if (!open || header.kitchen) return;
		const prefillLabel = modalData?.kitchenId
			? lists.kitchenList?.find(
				(k) => String(k?.value) === String(modalData.kitchenId)
			)?.label
			: null;
		if (prefillLabel) {
			handleFieldChange('kitchen', prefillLabel);
			return;
		}
		if (lists.kitchenList?.length === 1) {
			handleFieldChange('kitchen', lists.kitchenList[0]?.label);
		}
	}, [open, lists.kitchenList, header.kitchen]);

	useEffect(() => {
		setSearch('');
	}, [step]);

	useEffect(() => {
		if (open && !dateTouchedRef.current) {
			setHeader((prev) => ({
				...prev,
				planningDate: modalData?.date
					? new Date(modalData.date)
					: getDefaultPlanningDate(),
			}));
		}
	}, [open]);

	useEffect(() => {
		if (!open || planNameTouchedRef.current) return;
		setHeader((prev) => ({
			...prev,
			planName: modalData?.planName
				|| `Plan ${moment(prev.planningDate || new Date()).format('DD MMM')}`,
		}));
	}, [open, header.planningDate]);

	const columns = useMemo(() => buildColumns(header.planningDate), [header.planningDate]);

	const visibleColumns = useMemo(() => {
		const keys = stepColumnKeys[step];
		if (!keys) return columns;
		let cols = keys.map((k) => columns.find((c) => c.key === k)).filter(Boolean);
		const orderKeys = stepColumnOrder[step];
		if (orderKeys?.length) {
			cols = [...cols].sort((a, b) => {
				const ai = orderKeys.indexOf(a.key);
				const bi = orderKeys.indexOf(b.key);
				return (ai === -1 ? orderKeys.length : ai) - (bi === -1 ? orderKeys.length : bi);
			});
		}
		return cols.map((c) => {
			let col = (step === 2 && c.fixed) ? { ...c, fixed: false } : c;
			if (col.key === 'productionQty' && step === 2) {
				col = { ...col, title: 'AI Recommend Production Qty' };
			}
			if (col.key === 'sno') return col;
			if (col.key === 'productName') return { ...col, width: 200 };
			if (col.key === 'unpackedProductName') return { ...col, width: 180 };
			return { ...col, width: Math.min(col.width, 120) };
		});
	}, [columns, step]);

	// A row only counts as edited while some value actually differs from its
	// original (pre-edit) baseline — typing 23 → 12 → 23 ends up NOT edited, so
	// the footer shows Initiate again without requiring a recalculation.
	const normQty = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
	const applyCellEdit = (r, key, value) => {
		const orig = { ...(r?._orig || {}) };
		if (!(key in orig)) orig[key] = r?.[key] ?? null;
		const next = { ...r, [key]: value, _orig: orig };
		next._edited = Object.keys(orig).some((k) => normQty(orig[k]) !== normQty(next[k]));
		return next;
	};

	const updateCell = (rowId, key, value) => {
		setTableData((prev) => prev.map((r) => (r.id === rowId ? applyCellEdit(r, key, value) : r)));
	};

	// Rows left after search + SKU type + process filters. Everything that acts on
	// "what the user can see" (the table itself, Export Excel, Paste Excel) starts here.
	const filteredRows = useMemo(() => {
		const term = String(search || '').trim().toLowerCase();
		let rows = !term ? tableData : tableData.filter((r) =>
			String(r?.productName || '').toLowerCase().includes(term)
			|| String(r?.variantName || '').toLowerCase().includes(term));
		if (skuTypeFilter !== 'All') {
			rows = rows.filter((r) => String(r?.skuType || '').toUpperCase() === skuTypeFilter);
		}
		if (processFilter !== 'All') {
			rows = rows.filter((r) => String(r?.processName || '') === processFilter);
		}
		return rows;
	}, [tableData, search, skuTypeFilter, processFilter]);

	const isFiltered = Boolean(String(search || '').trim())
		|| skuTypeFilter !== 'All'
		|| processFilter !== 'All';

	const buildMatchPool = (rows) => ({
		flattenedProducts: rows.map((r) => ({ ...r, skuCode: r?.variantName })),
		productList: rows.map((r) => ({
			id: r?.productId,
			productName: r?.productName,
			variants: [{
				id: r?.variantId,
				displayName: r?.variantName,
				clientVariantId: r?.variantName,
				isDefault: true,
			}],
		})),
	});

	// Paste only ever writes into visible rows; the full pool is used solely to tell
	// "hidden by the current filter" apart from "not in this plan at all".
	const visibleMatchPool = useMemo(() => buildMatchPool(filteredRows), [filteredRows]);
	const allMatchPool = useMemo(() => buildMatchPool(tableData), [tableData]);

	const matchPastedRow = (name, sku, pool = visibleMatchPool) => matchPastedRowToProduct({
		name,
		sku,
		flattenedProducts: pool.flattenedProducts,
		productList: pool.productList,
		fuzzyMatcher: matchIngredientToProduct,
	});

	const convertPastedQty = (row, unitRaw, qty) => {
		if (row && unitRaw && getUnitFamily(row?.originalUnit) === getUnitFamily(unitRaw)) {
			const converted = Number(getHigherUnitAndQuantity(String(unitRaw).toLowerCase().trim(), qty)?.[0]);
			return Number.isFinite(converted) ? converted : qty;
		}
		return qty;
	};

	const applyPaste = (next, aiNext = {}, fieldsNext = {}) => {

		const lockedRows = tableData.filter((r) => aiNext[r.id] !== undefined && r?.isEditable === false);
		const aiAllowed = { ...aiNext };
		lockedRows.forEach((r) => delete aiAllowed[r.id]);

		const closingLockedRows = tableData.filter((r) => next[r.id] !== undefined
			&& isClosingStockLocked(r, fieldsNext[r.id]?.threshold));
		const closingAllowed = { ...next };
		closingLockedRows.forEach((r) => delete closingAllowed[r.id]);

		const ids = [...new Set([...Object.keys(closingAllowed), ...Object.keys(aiAllowed), ...Object.keys(fieldsNext)])].map(Number);
		if (lockedRows.length) {
			notificationServices.generateNotification({
				type: 'warning',
				message: `Skipped Production Qty for ${lockedRows.length} non-editable product(s): ${lockedRows.slice(0, 3).map((r) => r?.productName).join(', ')}${lockedRows.length > 3 ? '…' : ''}`,
			});
		}
		if (closingLockedRows.length) {
			notificationServices.generateNotification({
				type: 'warning',
				message: `Skipped Closing Stock for ${closingLockedRows.length} SFG product(s) without threshold: ${closingLockedRows.slice(0, 3).map((r) => r?.productName).join(', ')}${closingLockedRows.length > 3 ? '…' : ''}`,
			});
		}
		if (!ids.length) return;
		// Route every pasted value through applyCellEdit so the _orig baseline
		// tracks paste changes too — pasting values identical to the current
		// ones (or reverting later) leaves the row NOT edited.
		setTableData((prev) => prev.map((r) => {
			const hasClosing = closingAllowed[r.id] !== undefined;
			const hasAi = aiAllowed[r.id] !== undefined;
			const rowFields = fieldsNext[r.id];
			if (!hasClosing && !hasAi && !rowFields) return r;
			// Remember the ORIGINAL pre-paste closing value (first paste wins)
			// so the cell can show what was there before the paste.
			const prevClosing = r?._prevClosingStock ?? r?.closingStock ?? null;
			let row = r;
			if (hasClosing) {
				row = { ...applyCellEdit(row, 'closingStock', closingAllowed[r.id]), _prevClosingStock: prevClosing };
			}
			if (hasAi) {
				row = applyCellEdit(row, 'productionQty', aiAllowed[r.id]);
			}
			if (rowFields) {
				Object.entries(rowFields).forEach(([k, v]) => {
					row = applyCellEdit(row, k, v);
				});
			}
			return row;
		}));
		setPastedIds((prev) => [...new Set([...prev, ...ids])]);
		notificationServices.generateNotification({
			type: 'success',
			message: `Pasted values for ${ids.length} product(s)`,
		});
	};

	const handleExportExcel = () => {
		const headers = [];
		visibleColumns.forEach((col) => {
			headers.push(col.title);
			if (col.key === 'productName') {
				headers.push('SKU Code');
				headers.push('Unit');
			}
		});

		const rowsData = visibleRows.map((row, index) => {
			const rowCells = [];
			visibleColumns.forEach((col) => {
				if (col.key === 'sno') {
					rowCells.push(index + 1);
					return;
				}
				if (col.key === 'productName') {
					const name = row.productName || '';
					const variant = row.variantName || '';
					const fullName = variant ? `${name} (${variant})` : name;
					rowCells.push(`"${fullName.replace(/"/g, '""')}"`);
					rowCells.push(`"${variant.replace(/"/g, '""')}"`);
					rowCells.push(`"${(row.unit || '').replace(/"/g, '""')}"`);
					return;
				}
				if (col.key === 'unpackedProductName') {
					const name = row.unpackedProductName || '';
					const variant = row.unpackedVariantName || '';
					const fullName = name ? (variant ? `${name} (${variant})` : name) : '-';
					rowCells.push(`"${fullName.replace(/"/g, '""')}"`);
					return;
				}
				const val = row[col.key];
				if (val === null || val === undefined || val === '') {
					rowCells.push('-');
					return;
				}
				if (typeof val === 'number') {
					const num = Math.round(val * 1000) / 1000;
					rowCells.push(num);
					return;
				}
				rowCells.push(`"${String(val).replace(/"/g, '""')}"`);
			});
			return rowCells;
		});

		const csvContent = [headers.join(','), ...rowsData.map((e) => e.join(','))].join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.setAttribute('href', url);
		const fileName = `${header.planName || 'Production_Plan'}_Step_${step}.csv`;
		link.setAttribute('download', fileName);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handlePasteClosing = async () => {
		let text = '';
		try {
			text = await navigator.clipboard.readText();
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Could not read the clipboard. Copy the rows from Excel and try again.',
			});
			return;
		}
		const rows = parsePastedTable(text, ['closingStock', 'closing stock'], [
			{
				key: 'aiQty',
				aliases: [
					'ai recommend production qty', 'airecommendproductionqty',
					'ai recommended production qty', 'ai recommend production',
					'recommend production qty', 'production qty', 'productionqty',
					'final production',
				],
			},
			{
				key: 'unpackedClosingStock',
				aliases: ['unpacked closing stock', 'unpackedclosingstock'],
			},
			{
				key: 'unpackedThreshold',
				aliases: ['unpacked threshold', 'unpackedthreshold'],
			},
			{
				key: 'unpackedMaxStock',
				aliases: ['unpacked max stock', 'unpackedmaxstock'],
			},
			{
				key: 'threshold',
				aliases: ['threshold / min stock', 'thresholdminstock', 'threshold', 'min stock', 'minstock'],
			},
			{
				key: 'maxStock',
				aliases: ['recommend stock', 'recommendstock', 'max stock', 'maxstock'],
			},
		]);
		if (!rows.length) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'No valid rows found in the clipboard (expected: product name + closing stock / AI recommend production qty).',
			});
			return;
		}
		const next = {};
		const aiNext = {};
		const fieldsNext = {};
		const notFoundItems = [];
		const ambiguousItems = [];
		const hiddenItems = [];
		// Unpacked keys hidden for now: 'unpackedClosingStock', 'unpackedThreshold', 'unpackedMaxStock'
		const PASTE_FIELD_KEYS = ['threshold', 'maxStock'];
		rows.forEach(({ name, sku, unit, qty, aiQty, ...extras }) => {
			const { row, candidates } = matchPastedRow(name, sku);
			// Filter is on and this product only exists outside it -> ignore the row.
			if (!row && !candidates?.length && isFiltered) {
				const hidden = matchPastedRow(name, sku, allMatchPool);
				if (hidden?.row || hidden?.candidates?.length) {
					hiddenItems.push(cleanPastedName(name) || name);
					return;
				}
			}
			if (candidates && candidates.length) {
				ambiguousItems.push({ name: cleanPastedName(name) || name, unit, qty, candidates, extras });
				return;
			}
			if (!row) {
				notFoundItems.push({ name: cleanPastedName(name) || name, sku, unit, qty });
				return;
			}
			if (qty !== null && qty !== undefined && !Number.isNaN(qty)) {
				next[row.id] = convertPastedQty(row, unit, qty);
			}
			if (aiQty !== null && aiQty !== undefined && !Number.isNaN(aiQty)) {
				aiNext[row.id] = convertPastedQty(row, unit, aiQty);
			}
			const extraVals = {};
			PASTE_FIELD_KEYS.forEach((key) => {
				const val = extras[key];
				if (val !== null && val !== undefined && !Number.isNaN(val)) {
					extraVals[key] = convertPastedQty(row, unit, val);
				}
			});
			if (Object.keys(extraVals).length) {
				fieldsNext[row.id] = extraVals;
			}
		});

		if (ambiguousItems.length) {
			setAmbiguousRows((prev) => [
				...prev,
				...ambiguousItems.map((it, i) => ({
					id: `amb_${i}_${Date.now()}`,
					productName: it.name,
					unit: it.unit || '',
					pastedQty: it.qty,
					pastedExtras: it.extras || {},
					candidates: it.candidates,
					_ambiguous: true,
				})),
			]);
		}
		if (hiddenItems.length) {
			notificationServices.generateNotification({
				type: 'warning',
				message: `Ignored ${hiddenItems.length} row(s) hidden by the current filter: ${hiddenItems.slice(0, 3).join(', ')}${hiddenItems.length > 3 ? '…' : ''}`,
			});
		}
		if (!Object.keys(next).length && !Object.keys(aiNext).length && !Object.keys(fieldsNext).length && !ambiguousItems.length) {
			if (!hiddenItems.length) {
				notificationServices.generateNotification({ type: 'warning', message: 'No matching products found in clipboard' });
			}
			return;
		}
		applyPaste(next, aiNext, fieldsNext);
	};

	const closeNotFound = () => {
		setNotFoundModal({ isOpen: false, items: [] });
		setPendingPaste(null);
	};

	const handleFoundOnly = () => {
		if (pendingPaste?.next) applyPaste(pendingPaste.next, pendingPaste.aiNext, pendingPaste.fieldsNext);
		setNotFoundRows([]);
		closeNotFound();
	};

	const handleKeepAll = () => {
		if (pendingPaste?.next) applyPaste(pendingPaste.next, pendingPaste.aiNext, pendingPaste.fieldsNext);
		setNotFoundRows((notFoundModal.items || []).map((it, i) => ({
			id: `nf_${i}_${Date.now()}`,
			productName: it.name,
			variantName: it.sku || '-',
			unit: it.unit || '',
			pastedQty: it.qty,
			_notFound: true,
		})));
		closeNotFound();
	};

	const removeNotFoundRow = (id) => setNotFoundRows((prev) => prev.filter((r) => r.id !== id));

	const updateAmbiguousQty = (id, val) => {
		if (/[-+e]/i.test(val)) return;
		const trimmed = String(val ?? '').trim();
		if (trimmed !== '' && Number.isNaN(Number(trimmed))) return;
		setAmbiguousRows((prev) => prev.map((r) => (r.id === id
			? { ...r, pastedQty: trimmed === '' ? '' : Number(trimmed) }
			: r)));
	};

	const resolveAmbiguous = (ambId, variantId) => {
		const amb = ambiguousRows.find((r) => r.id === ambId);
		const row = tableData.find((r) => String(r?.variantId) === String(variantId));
		if (amb && row) {
			const qtyRaw = String(amb.pastedQty ?? '').trim();
			const qtyNum = qtyRaw === '' ? null : Number(qtyRaw);
			const hasQty = qtyNum !== null && Number.isFinite(qtyNum);
			const finalQty = hasQty ? convertPastedQty(row, amb.unit, qtyNum) : undefined;
			const extraVals = {};
			Object.entries(amb?.pastedExtras || {}).forEach(([key, val]) => {
				if (val !== null && val !== undefined && !Number.isNaN(val)) {
					extraVals[key] = convertPastedQty(row, amb.unit, val);
				}
			});
			const applyClosing = hasQty && !isClosingStockLocked(row, extraVals.threshold);
			setTableData((prev) => prev.map((r) => {
				if (r.id !== row.id) return r;
				const prevClosing = r?._prevClosingStock ?? r?.closingStock ?? null;
				let nextRow = r;
				if (applyClosing) {
					nextRow = { ...applyCellEdit(nextRow, 'closingStock', finalQty), _prevClosingStock: prevClosing };
				}
				Object.entries(extraVals).forEach(([k, v]) => {
					nextRow = applyCellEdit(nextRow, k, v);
				});
				return nextRow;
			}));
			setPastedIds((prev) => [...new Set([...prev, row.id])]);
		}
		setAmbiguousRows((prev) => prev.filter((r) => r.id !== ambId));
	};

	const customRowData = useMemo(() => {
		const editableKeys = editableColumnKeys[step] || [];
		const editable = (row, key) => (
			<InputCell key={`${row?.id}-${key}`} row={row} colKey={key} onCommit={updateCell} />
		);
		const renderers = {
			sno: (row, index, key, absoluteIndex) => (absoluteIndex ?? index) + 1,
			productName: (row) => {
				if (row?._ambiguous) {
					return (
						<div className='flex flex-col gap-1.5 items-start py-1'>
							<div className='flex items-center gap-1.5'>
								<AlertTriangle size={15} className='text-amber-600 flex-shrink-0' />
								<span className='text-amber-700 font-semibold'>{row?.productName}</span>
								<span className='bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap'>Multiple matches</span>
							</div>
							<select
								defaultValue=''
								onChange={(e) => e.target.value && resolveAmbiguous(row.id, e.target.value)}
								className='w-full max-w-[260px] border border-amber-300 rounded px-2 py-1 text-[12px] focus:outline-none focus:border-amber-500'
							>
								<option value='' disabled>Select product…</option>
								{(row?.candidates || []).map((c) => (
									<option key={c.variantId ?? c.productId} value={c.variantId ?? ''}>{c.label}</option>
								))}
							</select>
						</div>
					);
				}
				// Red paste row: not found in the plan — delete icon sits with the name.
				if (row?._notFound) {
					return (
						<div className='flex flex-col gap-1 items-start py-1'>
							<div className='flex items-center gap-1.5'>
								<span className='text-red-600 font-semibold'>{row?.productName}</span>
							</div>
							<span className='bg-red-100 text-red-700 text-[10px] font-semibold px-1.5 py-0.5 rounded'>Not found</span>
						</div>
					);
				}
				return (
					<div className='flex flex-col gap-0.5 items-start py-1'>
						<span className='font-medium'>
							{row?.productName}
							{row?.variantName ? ` (${row.variantName})` : ''}
						</span>
						<div className='flex items-center gap-1.5'>
							<span className='text-[11px] text-gray-400'>SKU · {row?.variantName || '-'}</span>
							{row?.skuType && (
								<span className='text-[10px] font-medium bg-gray-100 text-gray-500 px-1.5 py-[1px] rounded'>{row.skuType}</span>
							)}
						</div>
						{row?.hasRecipe === false && row?.productId && (
							<a
								href={`${urls.EXTENSION}${urls.COOKING_RECIPE}/edit/${row.productId}`}
								target='_blank'
								rel='noopener noreferrer'
								className='flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 hover:underline'
							>
								<PlusCircle size={12} />
								Add Recipe
							</a>
						)}
					</div>
				);
			},
			unpackedProductName: (row) => {
				if (row?._ambiguous || row?._notFound || !row?.unpackedProductName) return '-';
				return (
					<div className='flex flex-col gap-0.5 items-start py-1'>
						<span className='font-medium'>{row?.unpackedProductName}</span>
						{row?.unpackedVariantName && (
							<span className='text-[11px] text-gray-400'>SKU · {row.unpackedVariantName}</span>
						)}
					</div>
				);
			},
			closingStock: (row) => {
				if (row?._ambiguous) {
					return (
						<div className='flex items-center gap-1.5'>
							<input
								type='number'
								min='0'
								value={row?.pastedQty ?? ''}
								onKeyDown={(e) => {
									if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault();
								}}
								onChange={(e) => updateAmbiguousQty(row.id, e.target.value)}
								placeholder='0'
								className='w-[72px] border border-gray-200 rounded-lg px-2 py-1 text-[13px] text-center bg-white text-gray-800 transition-all placeholder:text-gray-300 hover:border-amber-300 focus:outline-none focus:border-amber-400 focus:shadow-[0_0_0_2px_rgba(251,191,36,0.15)]'
							/>
							{row?.unit && (
								<span className='text-[11px] font-medium text-gray-500 whitespace-nowrap'>{row.unit}</span>
							)}
						</div>
					);
				}
				if (row?._notFound) {
					return (
						<span className='text-[13px] font-semibold text-red-600'>
							{row?.pastedQty ?? '-'} {row?.unit || ''}
						</span>
					);
				}
				if (editableKeys.includes('closingStock')) {
					if (isClosingStockLocked(row)) return displayCell(row, 'closingStock');
					const hasPrev = step === 2 && row?._prevClosingStock !== undefined;
					return (
						<div className='flex flex-col items-start gap-0.5'>
							{hasPrev && (
								<span className='text-[10px] text-gray-400 whitespace-nowrap'>
									Prefilled: {displayCell({ ...row, closingStock: row?._prevClosingStock }, 'closingStock')}
								</span>
							)}
							{editable(row, 'closingStock')}
						</div>
					);
				}
				return displayCell(row, 'closingStock');
			},
			productionQty: (row) => {
				if (row?._notFound) {
					return (
						<button
							type='button'
							aria-label='Remove row'
							onClick={() => removeNotFoundRow(row.id)}
							className='flex items-center gap-1.5 text-red-500 hover:text-red-700 text-[12px] font-semibold'
						>
							<Trash2 size={15} />
							Remove
						</button>
					);
				}
				if (editableKeys.includes('productionQty') && row?.isEditable !== false) {
					return editable(row, 'productionQty');
				}
				return (row?.productionQty === null || row?.productionQty === undefined || row?.productionQty === '') ? '-' : (
					<span className='bg-amber-100 text-amber-800 font-semibold text-[13px] px-2.5 py-1 rounded-md whitespace-nowrap'>
						{displayCell(row, 'productionQty')}
					</span>
				);
			},
		};
		columns.forEach((col) => {
			if (renderers[col.key]) return;
			renderers[col.key] = (row) => {
				if (row?._notFound) return '-';
				if (editableKeys.includes(col.key)) {
					return editable(row, col.key);
				}
				// Recommend Stock (maxStock): show '-' when it is 0 / unset.
				if (
					col.key === 'maxStock' &&
					(row?.maxStock === null ||
						row?.maxStock === undefined ||
						row?.maxStock === '' ||
						Number(row?.maxStock) === 0)
				) {
					return '-';
				}
				return displayCell(row, col.key);
			};
		});
		return renderers;
	}, [columns, step, tableData, ambiguousRows]);

	const processColorMap = useMemo(() => buildProcessColorMap(tableData), [tableData]);

	const visibleRows = useMemo(() => {
		let rows = filteredRows;
		// Pasted rows pin to the top (in paste order).
		if (pastedIds.length) {
			const topSet = new Set(pastedIds);
			const top = [];
			const rest = [];
			rows.forEach((r) => (topSet.has(r.id) ? top.push(r) : rest.push(r)));
			top.sort((a, b) => pastedIds.indexOf(a.id) - pastedIds.indexOf(b.id));
			rows = [...top, ...rest];
		}
		rows = rows.map((r) => {
			const p = r?.processId ?? r?.processName;
			let row = (p != null && processColorMap[p])
				? { ...r, rowStyle: { backgroundColor: processColorMap[p] } }
				: r;
			// Products not scheduled for the plan date get one strike line across
			// the whole row (drawn per cell via ::after so it runs continuously,
			// over inputs too; sticky cells are already positioned).
			if (row?.notScheduled) {
				row = { ...row, rowClassName: NOT_SCHEDULED_ROW_CLASS };
			}
			return row;
		});
		// Unresolved paste rows (amber + red) sit above everything on step 2.
		if (step === 2 && (ambiguousRows.length || notFoundRows.length)) {
			rows = [...ambiguousRows, ...notFoundRows, ...rows];
		}
		return rows;
	}, [filteredRows, step, processColorMap, pastedIds, ambiguousRows, notFoundRows]);

	const processOptions = useMemo(() => {
		const names = Array.from(new Set((tableData || [])
			.map((r) => r?.processName)
			.filter(Boolean)));
		return ['All', ...names];
	}, [tableData]);

	const goToStep = (s) => {
		setStep(s);
		setMaxStep((m) => Math.max(m, s));
	};
	const stepOneValid = Boolean(header.planName && header.kitchen && header.planningDate && header.shift);

	const handleStepOneNext = async () => {
		if (!stepOneValid || loading) return;
		setLoading(true);
		try {
			const res = await productionPlanServices.productionPlanFetchQuanties({
				data: {
					storeId: kitchenIdOf(header.kitchen),
					shiftId: shiftIdOf(header.shift),
					date: moment(header.planningDate).format('YYYY-MM-DD'),
				},
			});
			if (res?.statusCode === 200) {
				const rows = (res?.data?.data || []).map(displayRowOf);
				setPlanId(res?.data?.planId ?? null);
				setTableData(rows);
				setStepTrack((prev) => ({ ...prev, 1: { ...header }, 2: rows }));
				goToStep(2);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Failed to load plan quantities.',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Failed to load plan quantities.',
			});
		} finally {
			setLoading(false);
		}
	};

	const toApiRow = (r) => {
		const { id, unit, originalUnit, skuType, _edited, _orig, rowStyle, rowClassName, _prevClosingStock, ...rest } = r || {};
		if (unit) {
			QTY_KEYS.forEach((k) => {
				if (rest[k] === null || rest[k] === undefined || rest[k] === '') return;
				rest[k] = getUnitInLowerUnit(unit, toNum(rest[k]));
			});
		}
		return rest;
	};
	// Recalculate (same call that used to run on "Next: Review"): sends the edited
	// rows, refreshes the table with the recalculated values and clears the
	// _edited flags — which flips the footer button back to Initiate.
	const handleRecalculate = async () => {
		if (!tableData.length) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'No products to review.',
			});
			return;
		}
		// Amber (multiple matches) / red (not found) paste rows must be resolved first.
		if (ambiguousRows.length || notFoundRows.length) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Resolve the highlighted pasted rows (multiple matches / not found) before continuing.',
			});
			return;
		}
		if (loading) return;
		const editedRows = tableData.filter((r) => r?._edited);
		if (!editedRows.length) return;
		setLoading(true);
		try {
			const res = await productionPlanServices.productionPlanEditQuanties({
				data: {
					planId,
					data: editedRows.map(toApiRow),
				},
			});
			if (res?.statusCode === 200) {
				const rowsRaw = res?.data?.data || (Array.isArray(res?.data) ? res.data : []);
				// Recalculated values become the new baseline — drop the _orig
				// snapshots along with the _edited flags.
				const nextRows = rowsRaw.length
					? rowsRaw.map(displayRowOf)
					: tableData.map(({ _orig, ...r }) => ({ ...r, _edited: false }));
				setTableData(nextRows);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Failed to recalculate plan quantities.',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Failed to recalculate plan quantities.',
			});
		} finally {
			setLoading(false);
		}
	};

	const handleInitiate = () => {
		if (!tableData.length) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'No products to initiate.',
			});
			return;
		}
		setConfirmOpen(true);
	};

	const handleConfirmGenerate = async () => {
		setLoading(true);
		const payload = {
			description: header.planName,
			storeId: kitchenIdOf(header.kitchen) ?? header.kitchen,
			shiftId: shiftIdOf(header.shift) ?? header.shift,
			date: moment(header.planningDate).format('YYYY-MM-DD'),
			...(planId ? { planId } : {}),
			items: tableData.map((r) => ({
				productId: r?.productId,
				variantId: r?.variantId,
				quantity: Number(getUnitInLowerUnit(r?.unit, toNum(r?.productionQty)).toFixed(3)),
			})),
		};
		try {
			const res = await productionPlanServices.generateProductionPlanQtys({ data: payload });
			if (res?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: res?.message || `Production plan "${header.planName}" initiated with ${tableData.length} item(s).`,
				});
				setConfirmOpen(false);
				const planData = res?.data;
				dispatch(modalActions.toggleProductionPlanModal(true, {
					listData: planData,
					date: payload.date,
					storeId: payload.storeId,
					shiftId: payload.shiftId,
					lppId: planData?.lppId,
					planId: planId,
					productionPlanId: planData?.productionPlanId ?? planData?.id ?? res?.id,
					planDescription: header.planName,
					editMode: true,
					finalProductionNonEditable: true,
				}));
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Failed to initiate production plan.',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Failed to initiate production plan.',
			});
		} finally {
			setLoading(false);
		}
	};
	const hasUnresolvedPaste = ambiguousRows.length > 0 || notFoundRows.length > 0;
	const hasEdits = tableData.some((r) => r?._edited);
	const footerButtons = buildFooterButtons({
		step,
		loading,
		stepOneValid,
		hasEdits,
		onBack: () => setStep((s) => Math.max(1, s - 1)),
		onNextStock: handleStepOneNext,
		onRecalculate: handleRecalculate,
		onInitiate: handleInitiate,
	}).map((btn) => (['recalculate', 'initiate'].includes(btn.id)
		? { ...btn, disabled: btn.disabled || hasUnresolvedPaste }
		: btn));

	const renderBtns = () => renderFooterButtons(footerButtons);

	return (
		<BaseModal
			open={!!open}
			heading='Daily Production Plan'
			width='100vw'
			height='100vh'
			showClose
			handleClose={handleClose}
			renderBtns={renderBtns}
			wrapperClassName='!overflow-hidden'
		>
			<ConfirmationModal
				isOpen={confirmOpen}
				heading='Generate Production Plan'
				message='Do you want to generate this production plan?'
				onClose={() => setConfirmOpen(false)}
				onConfirm={handleConfirmGenerate}
				isLoading={loading}
			/>
			<ConfirmationModal
				isOpen={notFoundModal.isOpen}
				heading='Items Not Found'
				width='w-[560px] max-w-[92vw]'
				onClose={closeNotFound}
				message={(
					<div className='text-left max-h-[220px] overflow-y-auto'>
						<p className='mb-2 text-[13px] text-gray-600'>
							These pasted rows did not match any product in this plan:
						</p>
						<ul className='list-disc pl-5 text-[13px] text-red-600'>
							{(notFoundModal.items || []).map((it, i) => (
								<li key={i}>{it.name} — {it.qty} {it.unit || ''}</li>
							))}
						</ul>
					</div>
				)}
				customButton={[
					{ label: 'Found Items Only', onClick: handleFoundOnly, className: 'bg-blue-600 hover:bg-blue-700 text-white' },
					{ label: 'Keep All Items', onClick: handleKeepAll, className: 'bg-gray-100 hover:bg-gray-200 text-gray-700' },
					{ label: 'Cancel', onClick: closeNotFound, className: 'text-gray-400 hover:text-gray-600' },
				]}
			/>
			<ProductionPlanModal
				onCloseModal={(productionPlanId, generated = false) => {
					if (generated) {
						handleClose();
						if (onInitiated) onInitiated();
						if (productionPlanId !== null && productionPlanId !== undefined) {
							history.push({
								pathname: `${urls.EXTENSION}${urls.PRODUCTION_PLANS}`,
								search: `id=${productionPlanId}`,
							});
						}
					}
				}}
			/>
			<div className='flex flex-col h-full text-left overflow-x-hidden'>
				<div className='py-4 px-4 border-b border-gray-100'>
					<Stepper variant='inline' steps={STEPS} currentStep={step} />
				</div>
				{step === 1 && (
					<div className='flex-1 min-h-0 overflow-y-auto flex justify-center pt-8 bg-[#f8f7f4]'>
						<div className='w-full max-w-[620px] border border-gray-200 rounded-xl p-6 bg-white h-fit shadow-sm'>
							<div className='mb-1 text-[16px] font-semibold text-gray-800'>Plan Details</div>
							<div className='mb-5 text-[13px] text-gray-500'>
								Choose the name, date, kitchen and shift for this production plan.
							</div>
							<MultiPOGeneralDetails
								fields={headerFields}
								lists={lists}
								externalState={header}
								onFieldChange={handleFieldChange}
								layoutConfig={headerLayoutConfig}
								viewMode={maxStep > 1}
							/>
						</div>
					</div>
				)}
				{step === 2 && (
					<>
						<div className='flex items-center gap-3 py-2 flex-wrap'>
							{renderChips(header)}
							<div className='flex items-center gap-2'>
								<span className='text-[12px] font-semibold text-gray-600'>SKU Type</span>
								<div className='flex rounded-md border border-gray-200 overflow-hidden'>
									{['All', 'FG', 'SFG'].map((opt) => (
										<button
											key={opt}
											type='button'
											onClick={() => setSkuTypeFilter(opt)}
											className={`px-3 py-1 text-[12px] font-semibold transition-colors ${skuTypeFilter === opt
												? 'bg-amber-400 text-black'
												: 'bg-white text-gray-500 hover:bg-gray-50'}`}
										>
											{opt}
										</button>
									))}
								</div>
							</div>
							{step === 2 && (
								<div className='ml-auto flex items-center gap-2'>
									<button
										type='button'
										onClick={handleExportExcel}
										className='flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-amber-400 hover:bg-amber-500 text-black text-[12px] font-semibold h-8 whitespace-nowrap'
									>
										<Download size={15} />
										Export Excel
									</button>
									<button
										type='button'
										onClick={handlePasteClosing}
										title='Paste rows copied from Excel. Include a header row with Product Name plus any of: Closing Stock, Threshold / Min Stock, Recommend Stock, AI Recommend Production Qty (SKU Code and UOM optional). Every column present is applied.'
										className='flex items-center gap-2 px-4 py-1.5 rounded-md bg-slate-700 hover:bg-slate-800 text-white text-[12px] font-semibold h-8 whitespace-nowrap'
									>
										<ClipboardPaste size={15} />
										Paste Excel
									</button>
									<FormulaPopover show={showFormula} onToggle={() => setShowFormula((v) => !v)} />
								</div>
							)}
							{step === 2 && (ambiguousRows.length > 0 || notFoundRows.length > 0) && (
								<div className='basis-full flex items-center gap-4 px-1 py-1 text-[12px] flex-wrap'>
									{ambiguousRows.length > 0 && (
										<span className='flex items-center gap-1'>
											<span className='w-3 h-3 rounded-sm bg-amber-100 border border-amber-300' />
											<span className='text-amber-700 font-medium'>Multiple matches — select the correct product</span>
										</span>
									)}
									{notFoundRows.length > 0 && (
										<span className='flex items-center gap-1'>
											<span className='w-3 h-3 rounded-sm bg-red-100 border border-red-300' />
											<span className='text-red-700 font-medium'>Not found — remove the red rows</span>
										</span>
									)}
									<span className='text-gray-500'>Resolve all highlighted rows before continuing.</span>
								</div>
							)}
							{processOptions.length > 1 && (
								<div className='basis-full flex items-center gap-2 flex-wrap'>
									<span className='text-[12px] font-semibold text-gray-600'>Process</span>
									<div className='flex flex-wrap rounded-md border border-gray-200 overflow-hidden'>
										{processOptions.map((opt) => (
											<button
												key={opt}
												type='button'
												onClick={() => setProcessFilter(opt)}
												className={`px-3 py-1 text-[12px] font-semibold transition-colors ${processFilter === opt
													? 'bg-amber-400 text-black'
													: 'bg-white text-gray-500 hover:bg-gray-50'}`}
											>
												{opt}
											</button>
										))}
									</div>
								</div>
							)}
							<div className='basis-full w-full pb-1'>
								<GlobalSearch
									key={`product-search-${step}`}
									searchDropDown={false}
									setSearchValue={setSearch}
									placeholderText='Search Products...'
									width='100%'
								/>
							</div>
						</div>

						<div className='flex-1 min-h-0 overflow-hidden border border-gray-200 rounded-lg mt-1'>
							<VariantTable
								data={visibleRows}
								columns={visibleColumns}
								customRowData={customRowData}
								customCol={renderCustomCol}
								virtualization
								headerCellClassName='bg-amber-200 text-gray-800 !text-[11px] !font-bold'
								headerRowClassName='bg-amber-200'
								customInfoLine='No products found'
							/>
						</div>
					</>
				)}
			</div>
		</BaseModal>
	);
};

export default InitiateProductionPlanModal;
